import React from 'react';

import type { IntlShape } from 'react-intl';

import {
	alignmentMessages,
	mediaAndEmbedToolbarMessages as toolbarMessages,
	blockMenuMessages,
	linkToolbarMessages,
	linkMessages,
	nativeEmbedToolbarMessages,
} from '@atlaskit/editor-common/messages';
import type {
	Command,
	DropdownOptionT,
	ExtractInjectionAPI,
	FloatingToolbarConfig,
	FloatingToolbarHandler,
	FloatingToolbarItem,
	LinkPickerOptions,
} from '@atlaskit/editor-common/types';
import {
	LINKPICKER_HEIGHT_IN_PX,
	RECENT_SEARCH_HEIGHT_IN_PX,
	RECENT_SEARCH_WIDTH_IN_PX,
} from '@atlaskit/editor-common/ui';
import type { NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import AlignTextCenterIcon from '@atlaskit/icon/core/align-text-center';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import AlignTextRightIcon from '@atlaskit/icon/core/align-text-right';
import BorderIcon from '@atlaskit/icon/core/border';
import ContentWrapLeftIcon from '@atlaskit/icon/core/content-wrap-left';
import ContentWrapRightIcon from '@atlaskit/icon/core/content-wrap-right';
import CopyIcon from '@atlaskit/icon/core/copy';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import {
	ALIGNMENT_VALUES,
	BUILTIN_TOOLBAR_KEYS,
	getParameters,
	type AlignmentValue,
	type EditorToolbarAction,
	type ManifestEditorToolbarActions,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';

import type {
	EditorPluginNativeEmbedsPlugin,
	EditorToolbarHandlersWithDefaults,
} from '../../nativeEmbedsPluginType';
import { getWidth } from '../commands/resize';
import { pluginKey } from '../plugin-state';
import { getSelectedNativeEmbedExtension } from '../utils/getSelectedNativeEmbedExtension';

import { getNativeEmbedAppearanceDropdown } from './appearance-menu';
import { AskRovoToolbarButton } from './ask-rovo-toolbar-button';
import { buildNativeEmbedEditUrlToolbar } from './edit-url-toolbar';
import { getMoreOptionsDropdown } from './more-options-dropdown';
import { buildToolbarHandlers } from './toolbar-handler-factory';

type DeleteHoverProps = Pick<
	DropdownOptionT<Command>,
	'onMouseEnter' | 'onMouseLeave' | 'onFocus' | 'onBlur'
>;

type DeleteHoverPropsFactory = (nodeType: NodeType) => DeleteHoverProps;

const ALIGNMENT_ICONS: Record<
	AlignmentValue,
	| typeof AlignTextLeftIcon
	| typeof AlignTextCenterIcon
	| typeof AlignTextRightIcon
	| typeof ContentWrapLeftIcon
	| typeof ContentWrapRightIcon
> = {
	left: AlignTextLeftIcon,
	center: AlignTextCenterIcon,
	right: AlignTextRightIcon,
	'wrap-left': ContentWrapLeftIcon,
	'wrap-right': ContentWrapRightIcon,
};

const getAlignmentLabel = (alignment: AlignmentValue, intl: IntlShape): string => {
	switch (alignment) {
		case 'left':
			return intl.formatMessage(alignmentMessages.alignLeft);
		case 'center':
			return intl.formatMessage(alignmentMessages.alignCenter);
		case 'right':
			return intl.formatMessage(alignmentMessages.alignRight);
		case 'wrap-left':
			return intl.formatMessage(toolbarMessages.wrapLeft);
		case 'wrap-right':
			return intl.formatMessage(toolbarMessages.wrapRight);
	}
};

const getAlignmentIcon = (alignment: AlignmentValue, intl: IntlShape): React.ReactElement => {
	const IconComponent = ALIGNMENT_ICONS[alignment];
	return (
		<IconComponent
			color="currentColor"
			spacing="spacious"
			label={getAlignmentLabel(alignment, intl)}
		/>
	);
};

const NativeEmbedDropdownIcon = ({
	icon: IconComponent,
	label,
}: {
	icon: React.ComponentType<NewCoreIconProps>;
	label: string;
}) => (
	<span
		style={{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			display: 'flex',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			alignItems: 'center',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			marginRight: token('space.negative.100'),
		}}
	>
		<IconComponent color="currentColor" label={label} spacing="none" />
	</span>
);

/**
 * Converts a custom EditorToolbarAction to a FloatingToolbarItem.
 * Uses customActionHandlers from manifest when resolving handler.
 */
function convertCustomActionToToolbarItem(
	action: EditorToolbarAction,
	customActionHandlers: EditorToolbarHandlersWithDefaults,
): FloatingToolbarItem<Command> {
	return {
		id: `native-embed-${action.key}`,
		type: 'button',
		title: action.label,
		showTitle: action.showTitle,
		icon: action.icon,
		disabled: action.disabled,
		onClick: () => {
			customActionHandlers[action.handlerKey as keyof EditorToolbarHandlersWithDefaults];
			return true;
		},
		focusEditoronEnter: true,
		tabIndex: null,
	};
}

/**
 * Creates a registry of built-in toolbar items keyed by their BUILTIN_TOOLBAR_KEYS.
 * Resolves handlers from manifest first, then falls back to default actions.
 */
function createBuiltinToolbarRegistry(
	handlers: EditorToolbarHandlersWithDefaults,
	parameters: NativeEmbedParameterValues,
	intl: IntlShape,
	schema: Schema,
	embedWidth?: number,
	lineLength?: number,
	api?: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin>,
): Record<string, FloatingToolbarItem<Command>> {
	// When the embed width is greater than or equal to the layout width,
	// only "center" alignment is valid — disable all other options.
	const shouldDisableNonCenterAlignments =
		embedWidth !== undefined && lineLength !== undefined && embedWidth >= lineLength;

	const alignmentOptions: DropdownOptionT<Command>[] = ALIGNMENT_VALUES.map((alignment) => ({
		id: `native-embed-alignment-${alignment}`,
		title: getAlignmentLabel(alignment, intl),
		onClick: handlers.onAlignmentClick(alignment),
		selected: parameters.alignment === alignment,
		disabled: shouldDisableNonCenterAlignments && alignment !== 'center',
		icon: getAlignmentIcon(alignment, intl),
	}));

	const alignmentDropdownTitle = getAlignmentLabel(parameters.alignment, intl);
	const AlignmentIconComponent = ALIGNMENT_ICONS[parameters.alignment];

	return {
		...(expValEquals('cc-maui-experiment-phase-2', 'isEnabled', true)
			? {
					[BUILTIN_TOOLBAR_KEYS.ASK_ROVO]: {
						type: 'custom',
						fallback: [],
						render: (view?: EditorView) =>
							view ? (
								<AskRovoToolbarButton
									/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
									onClick={() => handlers.onAskRovoClick()(view.state, view.dispatch)}
								/>
							) : null,
					},
				}
			: {}),
		[BUILTIN_TOOLBAR_KEYS.SUMMARISE]: {
			id: 'native-embed-summarise-button',
			type: 'button',
			title: intl.formatMessage(nativeEmbedToolbarMessages.summarise),
			showTitle: true,
			icon: AiGenerativeTextSummaryIcon,
			onClick: handlers.onSummariseClick(),
			focusEditoronEnter: true,
			tabIndex: null,
		},
		[BUILTIN_TOOLBAR_KEYS.REFRESH]: {
			id: 'native-embed-refresh-button',
			type: 'button',
			title: intl.formatMessage(nativeEmbedToolbarMessages.refresh),
			showTitle: true,
			icon: RefreshIcon,
			onClick: handlers.onRefreshClick(),
			focusEditoronEnter: true,
			tabIndex: null,
		},
		[BUILTIN_TOOLBAR_KEYS.EMBED]: {
			...getNativeEmbedAppearanceDropdown({
				intl,
				onAppearanceClick: handlers.onAppearanceClick,
			}),
		},
		[BUILTIN_TOOLBAR_KEYS.BORDER]: {
			id: 'native-embed-change-border-dropdown',
			type: 'dropdown',
			title: ' ',
			iconBefore: () => <NativeEmbedDropdownIcon icon={BorderIcon} label="border" />,
			options: [],
			onClick: () => {}, // TODO: CNS-23805 - Implement Border Change Dropdown
		},
		[BUILTIN_TOOLBAR_KEYS.ALIGNMENT]: {
			id: 'native-embed-alignment-dropdown',
			type: 'dropdown',
			tooltip: alignmentDropdownTitle,
			title: ' ',
			iconBefore: () => (
				<NativeEmbedDropdownIcon icon={AlignmentIconComponent} label={alignmentDropdownTitle} />
			),
			options: alignmentOptions,
		},
		[BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW]: {
			id: 'native-embed-open-new-window-button',
			type: 'button',
			title: intl.formatMessage(linkMessages.openLink),
			icon: LinkExternalIcon,
			iconFallback: LinkExternalIcon,
			onClick: handlers.onOpenInNewWindowClick(),
			focusEditoronEnter: true,
			tabIndex: null,
		},
		[BUILTIN_TOOLBAR_KEYS.EDIT_URL]: {
			id: 'native-embed-edit-url-button',
			type: 'button',
			title: intl.formatMessage(linkToolbarMessages.editLink),
			icon: EditIcon,
			onClick: handlers.onEditUrlClick(),
			focusEditoronEnter: true,
			tabIndex: null,
		},
		[BUILTIN_TOOLBAR_KEYS.EDIT]: {
			id: 'native-embed-edit-button',
			type: 'button',
			title: intl.formatMessage(linkToolbarMessages.editDatasourceStandalone),
			icon: EditIcon,
			onClick: handlers.onEditClick(),
		},
		[BUILTIN_TOOLBAR_KEYS.COPY]: {
			id: 'native-embed-copy-button',
			type: 'button',
			title: intl.formatMessage(blockMenuMessages.copy),
			icon: CopyIcon,
			onClick: handlers.onCopyClick(),
			focusEditoronEnter: true,
			tabIndex: null,
		},
		/**
		 * @private
		 * @deprecated Use COPY instead. Will be removed in a follow-up PR.
		 */
		[BUILTIN_TOOLBAR_KEYS.COPY_LINK]: {
			id: 'native-embed-copy-link-button',
			type: 'button',
			title: intl.formatMessage(blockMenuMessages.copyLink),
			icon: CopyIcon,
			onClick: handlers.onCopyLinkClick(),
			focusEditoronEnter: true,
			tabIndex: null,
		},
		[BUILTIN_TOOLBAR_KEYS.DELETE]: {
			id: 'native-embed-delete-button',
			type: 'button',
			title: intl.formatMessage(blockMenuMessages.deleteBlock),
			icon: DeleteIcon,
			onClick: handlers.onDeleteClick(),
			focusEditoronEnter: true,
			tabIndex: null,
		},
	};
}

/**
 * Resolves toolbar items from the manifest's items array.
 * Looks up each key in the built-in registry or customActions.
 */
function resolveToolbarItemsFromItems(
	items: string[],
	builtinRegistry: Record<string, FloatingToolbarItem<Command>>,
	customActions: Record<string, EditorToolbarAction> | undefined,
	editorToolbarHandlers: EditorToolbarHandlersWithDefaults,
): FloatingToolbarItem<Command>[] {
	const resolvedItems: FloatingToolbarItem<Command>[] = [];

	for (const key of items) {
		if (key === BUILTIN_TOOLBAR_KEYS.SEPARATOR) {
			resolvedItems.push({ type: 'separator', fullHeight: true });
		} else if (key in builtinRegistry) {
			resolvedItems.push(builtinRegistry[key]);
		} else if (customActions && key in customActions) {
			resolvedItems.push(
				convertCustomActionToToolbarItem(customActions[key], editorToolbarHandlers),
			);
		}
	}

	return resolvedItems;
}

/**
 * Converts manifest-defined toolbar actions to FloatingToolbarItems.
 * Uses `items` array with built-in registry + custom actions.
 */
function convertManifestActionsToToolbarItems(
	manifestActions: ManifestEditorToolbarActions,
	handlers: EditorToolbarHandlersWithDefaults,
	parameters: NativeEmbedParameterValues,
	intl: IntlShape,
	schema: Schema,
	embedWidth?: number,
	lineLength?: number,
	api?: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin>,
): FloatingToolbarItem<Command>[] {
	if (!manifestActions.items) {
		return [];
	}

	const builtinRegistry = createBuiltinToolbarRegistry(
		handlers,
		parameters,
		intl,
		schema,
		embedWidth,
		lineLength,
		api,
	);
	return resolveToolbarItemsFromItems(
		manifestActions.items,
		builtinRegistry,
		manifestActions.customActions,
		handlers,
	);
}

interface GetToolbarConfigProps {
	api?: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin>;
	linkPickerOptions?: LinkPickerOptions;
	lpLinkPicker?: boolean;
}

/**
 * Returns toolbar sizing configuration when the URL edit toolbar is active.
 */
const editUrlToolbarConfig = (
	showUrlToolbar: boolean,
	lpLinkPicker?: boolean,
): Partial<FloatingToolbarConfig> => {
	return showUrlToolbar
		? {
				height: lpLinkPicker ? LINKPICKER_HEIGHT_IN_PX : RECENT_SEARCH_HEIGHT_IN_PX,
				width: RECENT_SEARCH_WIDTH_IN_PX,
				forcePlacement: true,
			}
		: {};
};

/**
 * Default items of toolbar items when no manifest is provided.
 */
const DEFAULT_TOOLBAR_ITEMS: string[] = [
	BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
	BUILTIN_TOOLBAR_KEYS.SUMMARISE,
	BUILTIN_TOOLBAR_KEYS.REFRESH,
	BUILTIN_TOOLBAR_KEYS.SEPARATOR,
	BUILTIN_TOOLBAR_KEYS.EMBED,
	BUILTIN_TOOLBAR_KEYS.BORDER,
	BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
	BUILTIN_TOOLBAR_KEYS.SEPARATOR,
	BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
];

/**
 * Resolves the DOM element to anchor the toolbar to, so it stays underneath the
 * aligned embed (left/center/right/wrap-left/wrap-right) instead of the full-width wrapper.
 * Prefers .extension-container when present (as child or ancestor).
 */
export function getToolbarAnchorElement(node: Node | null): HTMLElement | undefined {
	if (!(node instanceof HTMLElement)) {
		return undefined;
	}
	const container =
		node.querySelector('.extension-container') ?? node.closest('.extension-container');
	return container instanceof HTMLElement ? container : node;
}

/**
 * Returns the default toolbar items when no manifest actions are provided.
 */
function getDefaultToolbarItems(
	handlers: EditorToolbarHandlersWithDefaults,
	parameters: NativeEmbedParameterValues,
	intl: IntlShape,
	schema: Schema,
	embedWidth?: number,
	lineLength?: number,
	api?: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin>,
): FloatingToolbarItem<Command>[] {
	const builtinRegistry = createBuiltinToolbarRegistry(
		handlers,
		parameters,
		intl,
		schema,
		embedWidth,
		lineLength,
		api,
	);
	return resolveToolbarItemsFromItems(DEFAULT_TOOLBAR_ITEMS, builtinRegistry, undefined, handlers);
}

export const getToolbarConfig =
	({ api, linkPickerOptions, lpLinkPicker }: GetToolbarConfigProps): FloatingToolbarHandler =>
	(state, intl, providerFactory, _activeConfigs) => {
		const selectedNativeEmbed = getSelectedNativeEmbedExtension(state);
		if (!selectedNativeEmbed) {
			return undefined;
		}

		const pluginState = pluginKey.getState(state);
		const isShowingUrlToolbar = pluginState?.showUrlToolbar ?? false;

		const hoverDecoration = api?.decorations?.actions.hoverDecoration;
		const nodeType = state.schema.nodes.extension;
		const deleteHoverPropsFactory: DeleteHoverPropsFactory = (nt: NodeType) => ({
			onMouseEnter: hoverDecoration?.(nt, true),
			onMouseLeave: hoverDecoration?.(nt, false),
			onFocus: hoverDecoration?.(nt, true),
			onBlur: hoverDecoration?.(nt, false),
		});

		const parameters = getParameters(selectedNativeEmbed.node.attrs.parameters);

		// Resolve manifest from facade — try URL first, then fall back to
		// experience ID extracted from the extensionKey. The URL-based lookup
		// can fail when the embed is reinserted (e.g. after a Rovo edit)
		// without macroParams being preserved.
		const facade = getNativeEmbedFacade();
		let manifest = parameters.url ? facade.getManifestByUrl(parameters.url) : undefined;

		if (!manifest) {
			const extensionKey: string | undefined = selectedNativeEmbed.node.attrs.extensionKey;
			const experienceId = extensionKey?.split(':')[1];
			if (experienceId) {
				manifest = facade.getManifestById(experienceId);
			}
		}

		const manifestActions = manifest?.uiConfig?.editorToolbarActions;

		// Determine handlers based on manifest, falling back to defaults
		const resolvedHandlers = buildToolbarHandlers(
			selectedNativeEmbed,
			manifest?.uiConfig.editorToolbarHandlers,
			api,
			parameters,
			intl,
			state.schema,
		);

		const getDomRef = (view: EditorView) => {
			try {
				const node = findDomRefAtPos(selectedNativeEmbed.pos, view.domAtPos.bind(view));
				return getToolbarAnchorElement(node);
			} catch {
				return undefined;
			}
		};

		// If URL toolbar is showing, render the edit URL toolbar instead of normal items
		if (isShowingUrlToolbar) {
			return {
				title: 'Native Embed floating toolbar',
				getDomRef,
				nodeType: state.schema.nodes.extension,
				offset: [0, 8],
				scrollable: false,
				...editUrlToolbarConfig(true, lpLinkPicker),
				items: [
					buildNativeEmbedEditUrlToolbar({
						api,
						linkPickerOptions,
						lpLinkPicker: lpLinkPicker ?? true,
						providerFactory,
						selectedNativeEmbed,
					}),
				],
			};
		}

		// Determine the embed's current width and the layout line length so we
		// can disable non-center alignment options when the embed is as wide as
		// (or wider than) the layout.
		const widthState = api?.width?.sharedState?.currentState();
		const lineLength = widthState?.lineLength || akEditorDefaultLayoutWidth;
		const embedWidth = getWidth(selectedNativeEmbed.node) ?? lineLength;

		// Convert manifest actions to FloatingToolbarItems, or fall back to default items
		const items: FloatingToolbarItem<Command>[] = manifestActions
			? convertManifestActionsToToolbarItems(
					manifestActions,
					resolvedHandlers,
					parameters,
					intl,
					state.schema,
					embedWidth,
					lineLength,
					api,
				)
			: getDefaultToolbarItems(
					resolvedHandlers,
					parameters,
					intl,
					state.schema,
					embedWidth,
					lineLength,
					api,
				);

		// Auto-append the "More Options" dropdown when there are items to show.
		// Default toolbar (no manifest): always show with default items.
		// Manifest toolbar: only show when moreItems is provided with items.
		const showMoreOptions = manifestActions?.moreItems
			? Boolean(manifestActions?.moreItems?.length)
			: true;

		if (showMoreOptions) {
			items.push(
				{ type: 'separator' },
				getMoreOptionsDropdown(
					api,
					selectedNativeEmbed,
					deleteHoverPropsFactory(nodeType),
					resolvedHandlers,
					parameters,
					intl,
					manifestActions?.moreItems as string[] | undefined,
					manifestActions?.customActions,
				),
			);
		}

		return {
			title: 'Native Embed floating toolbar',
			getDomRef,
			nodeType: state.schema.nodes.extension,
			offset: [0, 8],
			items,
		};
	};
